import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../auth/AuthContext.jsx';
import { ErrorBanner, FieldError } from '../components/ui/index.ts';
import { useServerErrorHandler } from '../hooks/useServerErrorHandler.ts';
import { useSubmitState } from '../hooks/useSubmitState.ts';
import '../styles/pages/auth.css';

/**
 * PasswordInput — uncontrolled show/hide wrapper. Spreads a rhf register()
 * result onto the input. Show/hide toggle preserved with the existing aria-label.
 */
function PasswordInput({ id, label, autoComplete, disabled, register, ariaInvalid, ariaDescribedBy }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="form-field">
      <label htmlFor={id}>{label}</label>
      <div className="password-input-wrapper">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          disabled={disabled}
          aria-invalid={ariaInvalid || undefined}
          aria-describedby={ariaDescribedBy}
          {...(register ?? {})}
        />
        <button
          type="button"
          className="password-toggle"
          onClick={() => setVisible(!visible)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );
}

const requestSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
});

const confirmSchema = z
  .object({
    code: z.string().min(1, 'Verification code is required'),
    newPassword: z
      .string()
      .min(1, 'Password is required')
      .min(8, 'Password must be at least 8 characters.'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match.',
  });

const REQUEST_RESOLVER = zodResolver(requestSchema);
const CONFIRM_RESOLVER = zodResolver(confirmSchema);

export default function ForgotPasswordPage() {
  const { forgotPassword, forgotPasswordSubmit, login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialEmail = location.state?.email || '';

  const [step, setStep] = useState('request'); // 'request' | 'confirm' | 'invited'
  const [requestedEmail, setRequestedEmail] = useState(initialEmail);
  const [isRequestSubmitting, setIsRequestSubmitting] = useState(false);
  const [isConfirmSubmitting, setIsConfirmSubmitting] = useState(false);

  const requestForm = useForm({
    resolver: REQUEST_RESOLVER,
    defaultValues: { email: initialEmail },
  });
  const {
    register: registerRequest,
    handleSubmit: handleRequestSubmit,
    formState: { errors: requestErrors },
  } = requestForm;

  const confirmForm = useForm({
    resolver: CONFIRM_RESOLVER,
    defaultValues: { code: '', newPassword: '', confirmPassword: '' },
  });
  const {
    register: registerConfirmField,
    handleSubmit: handleConfirmSubmit,
    formState: { errors: confirmErrors },
  } = confirmForm;

  const {
    bannerMessage: requestBanner,
    handleServerError: handleRequestServerError,
    clearBanner: clearRequestBanner,
  } = useServerErrorHandler(requestForm);

  const {
    bannerMessage: confirmBanner,
    handleServerError: handleConfirmServerError,
    clearBanner: clearConfirmBanner,
  } = useServerErrorHandler(confirmForm);

  const requestSubmitState = useSubmitState({
    isSubmitting: isRequestSubmitting,
    baseLabel: 'Send reset code',
    loadingLabel: 'Sending code...',
  });

  const confirmSubmitState = useSubmitState({
    isSubmitting: isConfirmSubmitting,
    baseLabel: 'Reset password',
    loadingLabel: 'Resetting password...',
  });

  // Hooks above this line, conditional returns below.
  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  async function onRequestSubmit(values) {
    clearRequestBanner();
    setIsRequestSubmitting(true);
    try {
      const response = await forgotPassword(values.email);
      setRequestedEmail(values.email);
      if (response?.method === 'invite') {
        setStep('invited');
      } else {
        setStep('confirm');
      }
    } catch (err) {
      handleRequestServerError(err);
    } finally {
      setIsRequestSubmitting(false);
    }
  }

  async function onConfirmSubmit(values) {
    clearConfirmBanner();
    setIsConfirmSubmitting(true);
    try {
      await forgotPasswordSubmit(requestedEmail, values.code, values.newPassword);
      await login(requestedEmail, values.newPassword);
      navigate('/');
    } catch (err) {
      handleConfirmServerError(err);
    } finally {
      setIsConfirmSubmitting(false);
    }
  }

  if (step === 'invited') {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title">Invitation sent</h1>
          <p className="auth-subtitle">
            A new invitation email has been sent to <strong>{requestedEmail}</strong>. Check your inbox and
            follow the link to set your password.
          </p>
          <p className="auth-link">
            <Link to="/login">Back to sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title">Reset your password</h1>
          <p className="auth-subtitle">
            We sent a verification code to <strong>{requestedEmail}</strong>. Enter it below with your new password.
          </p>

          <form
            onSubmit={handleConfirmSubmit(onConfirmSubmit)}
            data-testid="forgot-form"
            noValidate
          >
            {confirmBanner && (
              <ErrorBanner message={confirmBanner} onDismiss={clearConfirmBanner} />
            )}

            <div className="form-field">
              <label htmlFor="code">Verification code</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                disabled={isConfirmSubmitting}
                aria-invalid={!!confirmErrors.code || undefined}
                aria-describedby={confirmErrors.code ? 'code-error' : undefined}
                {...registerConfirmField('code')}
              />
              <FieldError id="code-error" error={confirmErrors.code} />
            </div>

            <PasswordInput
              id="new-password"
              label="New password"
              autoComplete="new-password"
              disabled={isConfirmSubmitting}
              register={registerConfirmField('newPassword')}
              ariaInvalid={!!confirmErrors.newPassword}
              ariaDescribedBy={confirmErrors.newPassword ? 'new-password-error' : undefined}
            />
            <FieldError id="new-password-error" error={confirmErrors.newPassword} testId="newPassword-error" />

            <PasswordInput
              id="confirm-password"
              label="Confirm password"
              autoComplete="new-password"
              disabled={isConfirmSubmitting}
              register={registerConfirmField('confirmPassword')}
              ariaInvalid={!!confirmErrors.confirmPassword}
              ariaDescribedBy={confirmErrors.confirmPassword ? 'confirm-password-error' : undefined}
            />
            <FieldError id="confirm-password-error" error={confirmErrors.confirmPassword} testId="confirmPassword-error" />

            <button
              type="submit"
              data-testid="submit-btn"
              className="btn-primary btn-full"
              aria-busy={confirmSubmitState.ariaBusy}
              disabled={confirmSubmitState.ariaBusy}
            >
              {confirmSubmitState.buttonLabel}
            </button>
          </form>

          <p className="auth-link">
            <Link to="/login">Back to sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">Forgot your password?</h1>
        <p className="auth-subtitle">
          Enter your email address — we'll send you a code to reset your password, or if you're still waiting to activate your account, we'll resend your invite email.
        </p>

        <form
          onSubmit={handleRequestSubmit(onRequestSubmit)}
          data-testid="forgot-form"
          noValidate
        >
          {requestBanner && (
            <ErrorBanner message={requestBanner} onDismiss={clearRequestBanner} />
          )}

          <div className="form-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              disabled={isRequestSubmitting}
              aria-invalid={!!requestErrors.email || undefined}
              aria-describedby={requestErrors.email ? 'email-error' : undefined}
              {...registerRequest('email')}
            />
            <FieldError id="email-error" error={requestErrors.email} />
          </div>

          <button
            type="submit"
            data-testid="submit-btn"
            className="btn-primary btn-full"
            aria-busy={requestSubmitState.ariaBusy}
            disabled={requestSubmitState.ariaBusy}
          >
            {requestSubmitState.buttonLabel}
          </button>
        </form>

        <p className="auth-link">
          Remember your password?{' '}
          <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
